// modules/Multiplayer/MultiplayerUI.js
// 多人游戏UI模块 - 负责创建和显示UI元素

import UIStyleHelper from '../UIStyleHelper.js';
import BackgroundHelper from '../BackgroundHelper.js';
import ServerConfig from '../ServerConfig.js';

export default class MultiplayerUI {
    constructor(scene) {
        this.scene = scene;
        this.inputFields = {};
        this.statusText = null;
        this.connectButton = null;
        this.roomListContainer = null;
        this.refreshButton = null;
        this.createRoomButton = null;
    }
    
    /**
     * 创建所有UI元素
     * @param {Object} config - 配置对象 {playerName, serverUrl}
     */
    create(config = {}) {
        // 设置背景
        BackgroundHelper.createSolidBackground(this.scene, 0x1a1a2e);
        
        // 创建标题
        UIStyleHelper.createTitle(this.scene, '多人游戏', 80);
        
        // 创建输入表单
        this.createInputForm(config);
        
        // 创建连接服务器按钮
        this.createConnectButton();
        
        // 创建房间列表区域
        this.createRoomListArea();
        
        // 创建状态显示
        this.createStatusDisplay();
        
        // 创建返回按钮
        this.createBackButton();
    }
    
    /**
     * 创建输入表单
     * @param {Object} config - 配置对象
     */
    createInputForm(config) {
        const centerX = this.scene.cameras.main.centerX;
        const startY = this.scene.cameras.main.centerY - 100;
        const spacing = 60;
        
        // 玩家名称输入
        this.createInputField('playerName', '玩家名称:', config.playerName || '玩家', centerX, startY);
        
        // 服务器地址输入（使用环境配置）
        this.createInputField('serverUrl', '服务器地址:', config.serverUrl || ServerConfig.getDefaultServerUrl(), centerX, startY + spacing);
    }
    
    /**
     * 创建输入字段
     * @param {string} key - 字段键名
     * @param {string} label - 标签文本
     * @param {string} defaultValue - 默认值
     * @param {number} x - X坐标
     * @param {number} y - Y坐标
     * @param {Function} onEdit - 编辑回调
     */
    createInputField(key, label, defaultValue, x, y, onEdit = null) {
        // 标签
        const labelText = this.scene.add.text(
            x - 150,
            y,
            label,
            {
                fontSize: '16px',
                fill: '#ffffff',
                fontFamily: 'monospace'
            }
        );
        labelText.setOrigin(1, 0.5);
        
        // 输入框背景
        const inputBg = this.scene.add.graphics();
        inputBg.fillStyle(0x2a2a3e, 1);
        inputBg.fillRoundedRect(x - 100, y - 15, 200, 30, 5);
        inputBg.lineStyle(2, 0x4cc9f0, 1);
        inputBg.strokeRoundedRect(x - 100, y - 15, 200, 30, 5);
        
        // 输入文本
        const inputText = this.scene.add.text(
            x - 95,
            y,
            defaultValue,
            {
                fontSize: '14px',
                fill: '#ffffff',
                fontFamily: 'monospace',
                backgroundColor: 'transparent',
                fixedWidth: 190,
                wordWrap: { width: 190 }
            }
        );
        inputText.setOrigin(0, 0.5);
        inputText.setInteractive({ useHandCursor: true });
        
        // 点击输入框时，可以编辑
        inputText.on('pointerdown', () => {
            if (onEdit) {
                onEdit(key, label, inputText.text);
            }
        });
        
        this.inputFields[key] = {
            label: labelText,
            bg: inputBg,
            text: inputText,
            value: defaultValue
        };
    }
    
    /**
     * 更新输入字段值
     * @param {string} key - 字段键名
     * @param {string} value - 新值
     */
    updateInputField(key, value) {
        if (this.inputFields[key]) {
            this.inputFields[key].value = value;
            this.inputFields[key].text.setText(value);
        }
    }
    
    /**
     * 获取输入字段值
     * @param {string} key - 字段键名
     * @returns {string} 字段值
     */
    getInputFieldValue(key) {
        return this.inputFields[key]?.value || '';
    }
    
    /**
     * 创建连接服务器按钮
     * @param {Function} onClick - 点击回调
     */
    createConnectButton(onClick = null) {
        this.connectButton = this.scene.add.text(
            this.scene.cameras.main.centerX,
            this.scene.cameras.main.centerY + 20,
            '连接服务器',
            {
                fontSize: '18px',
                fill: '#ffffff',
                fontFamily: 'monospace',
                backgroundColor: '#4cc9f0',
                padding: { x: 25, y: 10 }
            }
        );
        this.connectButton.setOrigin(0.5);
        this.connectButton.setInteractive({ useHandCursor: true });
        
        if (onClick) {
            this.connectButton.on('pointerdown', onClick);
        }
    }
    
    /**
     * 设置连接按钮回调
     * @param {Function} onClick - 点击回调
     */
    setConnectButtonCallback(onClick) {
        if (this.connectButton && onClick) {
            this.connectButton.off('pointerdown');
            this.connectButton.on('pointerdown', onClick);
        }
    }
    
    /**
     * 更新连接按钮状态
     * @param {string} text - 按钮文本
     * @param {string} color - 文本颜色
     * @param {boolean} interactive - 是否可交互
     */
    updateConnectButton(text, color, interactive = true) {
        // 检查场景是否仍然活跃
        if (!this.scene || !this.scene.scene || !this.scene.scene.isActive()) {
            return; // 场景已销毁或未激活，不更新
        }
        
        if (this.connectButton && this.connectButton.active) {
            try {
                this.connectButton.setText(text);
                this.connectButton.setStyle({ fill: color });
                this.connectButton.setInteractive(interactive);
            } catch (error) {
                // 如果更新失败（可能对象已销毁），静默忽略
                console.warn('无法更新连接按钮:', error);
            }
        }
    }
    
    /**
     * 创建房间列表区域
     */
    createRoomListArea() {
        const centerX = this.scene.cameras.main.centerX;
        const centerY = this.scene.cameras.main.centerY;
        
        // 房间列表弹窗背景（居中显示）
        const dialogWidth = 700;
        const dialogHeight = 500;
        const dialogX = centerX - dialogWidth / 2;
        const dialogY = centerY - dialogHeight / 2;
        
        // 半透明背景遮罩
        const overlay = this.scene.add.graphics();
        overlay.fillStyle(0x000000, 0.7);
        overlay.fillRect(0, 0, this.scene.cameras.main.width, this.scene.cameras.main.height);
        overlay.setVisible(false);
        overlay.setDepth(5);
        
        // 弹窗背景
        const listBg = this.scene.add.graphics();
        listBg.fillStyle(0x1a1a2e, 0.95);
        listBg.fillRoundedRect(
            dialogX,
            dialogY,
            dialogWidth,
            dialogHeight,
            15
        );
        listBg.lineStyle(3, 0x4cc9f0, 1);
        listBg.strokeRoundedRect(
            dialogX,
            dialogY,
            dialogWidth,
            dialogHeight,
            15
        );
        listBg.setVisible(false);
        listBg.setDepth(6);
        
        // 房间列表标题
        const listTitle = this.scene.add.text(
            centerX,
            dialogY + 40,
            '房间列表',
            {
                fontSize: '28px',
                fill: '#4cc9f0',
                fontFamily: 'monospace',
                fontWeight: 'bold'
            }
        );
        listTitle.setOrigin(0.5);
        listTitle.setVisible(false);
        listTitle.setDepth(7);
        
        // 按钮容器（在标题下方）
        const buttonY = dialogY + 90;
        
        // 创建房间按钮
        this.createRoomButton = this.scene.add.text(
            centerX - 150,
            buttonY,
            '创建房间',
            {
                fontSize: '16px',
                fill: '#ffffff',
                fontFamily: 'monospace',
                backgroundColor: '#00aa00',
                padding: { x: 20, y: 10 }
            }
        );
        this.createRoomButton.setOrigin(0.5);
        this.createRoomButton.setInteractive({ useHandCursor: true });
        this.createRoomButton.setVisible(false);
        this.createRoomButton.setDepth(7);
        
        // 刷新按钮
        this.refreshButton = this.scene.add.text(
            centerX + 150,
            buttonY,
            '刷新',
            {
                fontSize: '16px',
                fill: '#ffffff',
                fontFamily: 'monospace',
                backgroundColor: '#4cc9f0',
                padding: { x: 20, y: 10 }
            }
        );
        this.refreshButton.setOrigin(0.5);
        this.refreshButton.setInteractive({ useHandCursor: true });
        this.refreshButton.setVisible(false);
        this.refreshButton.setDepth(7);
        
        // 房间列表内容区域（在按钮下方）
        const listContentY = dialogY + 140;
        const listContentHeight = dialogHeight - 180;
        
        
        this.roomListContainer = {
            overlay: overlay,
            bg: listBg,
            title: listTitle,
            dialogX: dialogX,
            dialogY: dialogY,
            dialogWidth: dialogWidth,
            dialogHeight: dialogHeight,
            listContentY: listContentY,
            listContentHeight: listContentHeight,
            items: []
        };
        
        // 初始提示文本
        const emptyText = this.scene.add.text(
            centerX,
            centerY,
            '请先连接服务器以查看房间列表',
            {
                fontSize: '18px',
                fill: '#888888',
                fontFamily: 'monospace'
            }
        );
        emptyText.setOrigin(0.5);
        emptyText.setVisible(false);
        emptyText.setDepth(7);
        this.roomListContainer.emptyText = emptyText;
    }
    
    /**
     * 设置创建房间按钮回调
     * @param {Function} onClick - 点击回调
     */
    setCreateRoomButtonCallback(onClick) {
        if (this.createRoomButton && onClick) {
            this.createRoomButton.off('pointerdown');
            this.createRoomButton.on('pointerdown', onClick);
        }
    }
    
    /**
     * 设置刷新按钮回调
     * @param {Function} onClick - 点击回调
     */
    setRefreshButtonCallback(onClick) {
        if (this.refreshButton && onClick) {
            this.refreshButton.off('pointerdown');
            this.refreshButton.on('pointerdown', onClick);
        }
    }
    
    /**
     * 创建状态显示
     */
    createStatusDisplay() {
        this.statusText = this.scene.add.text(
            this.scene.cameras.main.centerX,
            this.scene.cameras.main.centerY + 140,
            '状态: 未连接',
            {
                fontSize: '14px',
                fill: '#aaaaaa',
                fontFamily: 'monospace'
            }
        );
        this.statusText.setOrigin(0.5);
    }
    
    /**
     * 更新状态显示
     * @param {string} message - 状态消息
     * @param {string} color - 文本颜色
     */
    updateStatus(message, color = '#aaaaaa') {
        // 检查场景是否仍然活跃且文本对象有效
        if (!this.scene || !this.scene.scene || !this.scene.scene.isActive()) {
            return; // 场景已销毁或未激活，不更新
        }
        
        if (this.statusText && this.statusText.active) {
            try {
                this.statusText.setText(`状态: ${message}`);
                this.statusText.setStyle({ fill: color });
            } catch (error) {
                // 如果更新失败（可能对象已销毁），静默忽略
                console.warn('无法更新状态文本:', error);
            }
        }
    }
    
    /**
     * 创建返回按钮
     * @param {Function} onClick - 点击回调
     */
    createBackButton(onClick = null) {
        const backButton = this.scene.add.text(
            this.scene.cameras.main.centerX,
            this.scene.cameras.main.height - 50,
            '返回主菜单 (ESC)',
            {
                fontSize: '16px',
                fill: '#888888',
                fontFamily: 'monospace',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                padding: { x: 15, y: 8 }
            }
        );
        backButton.setOrigin(0.5);
        backButton.setInteractive({ useHandCursor: true });
        
        if (onClick) {
            backButton.on('pointerdown', onClick);
        }
    }
}

